import { Box, Typography, useTheme } from "@mui/material";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency, formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import { Chart as ChartJS } from "chart.js/auto";
import AnnotationPlugin from "chartjs-plugin-annotation";
import type React from "react";
import { Bar } from "react-chartjs-2";

ChartJS.register(AnnotationPlugin);

type Props = {
  readonly netIncomeAmount: number;
  readonly statePensionAmount: number;
};

const DATAPOINT_COUNT = 4;

export const PensionDifferenceChart: React.FC<Props> = ({ netIncomeAmount, statePensionAmount }) => {
  const theme = useTheme();
  const { t } = useResource();

  const differenceAmount = netIncomeAmount - (statePensionAmount || 0);
  const shouldDisplayDifferenceAmountText = differenceAmount / netIncomeAmount > 0.3;
  const shouldDisplayStatePensionAmountText = statePensionAmount > 0 && statePensionAmount / netIncomeAmount > 0.3;

  const options = {
    barPercentage: 0.8,
    responsive: true,
    maintainAspectRatio: false,
    borderRadius: {
      topLeft: 6,
      topRight: 6,
      bottomLeft: 6,
      bottomRight: 6,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || "";

            if (label) {
              const datasetName = context.dataset.label;

              switch (datasetName) {
                case t(Resources.AnalysisDetail.Goals.Income): {
                  return `${t(Resources.AnalysisDetail.Goals.Income)}: ${formatCurrency(context.dataset.data[context.dataIndex], CurrencyCode.CZK, 0)}`;
                }
                case t(Resources.AnalysisDetail.Goals.Pension): {
                  return `${t(Resources.AnalysisDetail.Goals.Pension)}: ${formatCurrency(context.dataset.data[context.dataIndex], CurrencyCode.CZK, 0)}`;
                }
                case t(Resources.AnalysisDetail.Goals.Difference): {
                  return `${t(Resources.AnalysisDetail.Goals.Difference)}: ${formatCurrency(Number(context.dataset.data[context.dataIndex]) - Number(statePensionAmount || 0), CurrencyCode.CZK, 0)}`;
                }
                default:
                  throw new Error();
              }
            }
          },
        },
      },
      annotation: {
        annotations: {
          netIncomeAmountLine: {
            type: "line" as const,
            yMin: netIncomeAmount,
            yMax: netIncomeAmount,
            xMin: -1,
            xMax: DATAPOINT_COUNT,
            borderWidth: 1,
            borderDash: [8, 4],
            borderColor: "rgba(0, 0, 0, 0.05)",
            label: {
              display: true,
              backgroundColor: "transparent",
              color: colors.textSecondary,
              position: "start" as const,
              yAdjust: 10,
              font: {
                weight: 400,
                size: 10,
              },
              content: formatCurrencyShortText(netIncomeAmount),
            },
          },
          netIncomeAmountTitle: {
            type: "line" as const,
            yMin: netIncomeAmount,
            yMax: netIncomeAmount,
            xMin: 1,
            xMax: 1,
            borderWidth: 1,
            borderDash: [8, 4],
            borderColor: "rgba(0, 0, 0, 0.05)",
            label: {
              display: true,
              backgroundColor: "transparent",
              color: colors.white,
              position: "start" as const,
              yAdjust: 20,
              font: {
                weight: 600,
                size: 12,
              },
              content: t(Resources.AnalysisDetail.Goals.Income),
            },
          },
          netIncomeAmountText: {
            type: "line" as const,
            yMin: netIncomeAmount,
            yMax: netIncomeAmount,
            xMin: 1,
            xMax: 1,
            borderWidth: 1,
            borderDash: [8, 4],
            borderColor: "rgba(0, 0, 0, 0.05)",
            label: {
              display: true,
              backgroundColor: "transparent",
              color: colors.white,
              position: "start" as const,
              yAdjust: 36,
              font: {
                weight: 200,
                size: 12,
              },
              content: `${formatCurrency(netIncomeAmount, CurrencyCode.CZK, 0)}`,
            },
          },
          statePensionAmountLine:
            (statePensionAmount && {
              type: "line" as const,
              yMin: statePensionAmount,
              yMax: statePensionAmount,
              xMin: -1,
              xMax: DATAPOINT_COUNT,
              borderWidth: 1,
              borderDash: [8, 4],
              borderColor: "rgba(0, 0, 0, 0.05)",
              label: {
                display: true,
                backgroundColor: "transparent",
                color: colors.textSecondary,
                position: "start" as const,
                yAdjust: 10,
                font: {
                  weight: 400,
                  size: 10,
                },
                content: formatCurrencyShortText(statePensionAmount),
              },
            }) ||
            undefined,
          differenceAmountTitle:
            (shouldDisplayDifferenceAmountText && {
              type: "line" as const,
              yMin: netIncomeAmount,
              yMax: netIncomeAmount,
              xMin: 2,
              xMax: 2,
              borderWidth: 1,
              borderDash: [8, 4],
              borderColor: "rgba(0, 0, 0, 0.05)",
              label: {
                display: true,
                backgroundColor: "transparent",
                color: colors.white,
                position: "start" as const,
                yAdjust: 20,
                font: {
                  weight: 600,
                  size: 12,
                },
                content: t(Resources.AnalysisDetail.Goals.Difference),
              },
            }) ||
            undefined,
          differenceAmountText:
            (shouldDisplayDifferenceAmountText && {
              type: "line" as const,
              yMin: netIncomeAmount,
              yMax: netIncomeAmount,
              xMin: 2,
              xMax: 2,
              borderWidth: 1,
              borderDash: [8, 4],
              borderColor: "rgba(0, 0, 0, 0.05)",
              label: {
                display: true,
                backgroundColor: "transparent",
                color: colors.white,
                position: "start" as const,
                yAdjust: 36,
                font: {
                  weight: 200,
                  size: 12,
                },
                content: `-${formatCurrency(differenceAmount, CurrencyCode.CZK, 0)}`,
              },
            }) ||
            undefined,
          statePensionAmountTitle:
            (shouldDisplayStatePensionAmountText && {
              type: "line" as const,
              yMin: statePensionAmount,
              yMax: statePensionAmount,
              xMin: 2,
              xMax: 2,
              borderWidth: 1,
              borderDash: [8, 4],
              borderColor: "rgba(0, 0, 0, 0.05)",
              label: {
                display: true,
                backgroundColor: "transparent",
                color: colors.white,
                position: "start" as const,
                yAdjust: 20,
                font: {
                  weight: 600,
                  size: 12,
                },
                content: t(Resources.AnalysisDetail.Goals.Pension),
              },
            }) ||
            undefined,
          statePensionAmountText:
            (shouldDisplayStatePensionAmountText && {
              type: "line" as const,
              yMin: statePensionAmount,
              yMax: statePensionAmount,
              xMin: 2,
              xMax: 2,
              borderWidth: 1,
              borderDash: [8, 4],
              borderColor: "rgba(0, 0, 0, 0.05)",
              label: {
                display: true,
                backgroundColor: "transparent",
                color: colors.white,
                position: "start" as const,
                yAdjust: 36,
                font: {
                  weight: 200,
                  size: 12,
                },
                content: `${formatCurrency(statePensionAmount, CurrencyCode.CZK, 0)}`,
              },
            }) ||
            undefined,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: colors.textSecondary,
        },
      },
      y: {
        beginAtZero: true,
        stacked: false,
        ticks: {
          display: false,
        },
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        grace: "1",
      },
    },
  };

  return (
    <Box>
      <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary">
        {t(Resources.AnalysisDetail.Goals.MonthlyIncome)}
      </Typography>
      <Box display="flex" alignItems="end">
        <Box position="relative" width={"100%"} minHeight={210}>
          <Bar
            options={options}
            data={{
              labels: new Array(DATAPOINT_COUNT).fill(""),
              datasets: [
                {
                  label: t(Resources.AnalysisDetail.Goals.Income),
                  data: [0, netIncomeAmount, 0, 0, 0],
                  backgroundColor: colors.chartPositive,
                },
                {
                  label: t(Resources.AnalysisDetail.Goals.Pension),
                  data: [0, 0, statePensionAmount, 0, 0],
                  backgroundColor: colors.brandPrimary,
                  order: 1,
                },
                {
                  label: t(Resources.AnalysisDetail.Goals.Difference),
                  data: [0, 0, netIncomeAmount, 0, 0],
                  backgroundColor: theme.palette.error.main,
                  order: 2,
                },
              ],
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
